import { useLazyQuery, useMutation } from '@apollo/client'
import { Delete, Download, UploadFile } from '@mui/icons-material'
import {
    Alert,
    AlertTitle,
    Box,
    CircularProgress,
    Grid,
    IconButton,
    Link,
    Stack,
    Typography,
} from '@mui/material'
import config from 'config'
import React, { Fragment, useCallback, useState } from 'react'
import { ErrorCode, useDropzone } from 'react-dropzone'
import { useParams } from 'react-router-dom'
import {
    DeleteCompanyDocument,
    DownloadCompanyDocument,
} from 'recruiter/graphql/companies.gql'
import useToast from 'shared/components/Toast/useToast'
import useIsMobile from 'shared/hooks/useIsMobile'
import theme from 'shared/style/theme'
import { getPxAsRem } from 'shared/utils'
import ConfirmDialog from './ConfirmDialog'

import type {
    CompanyDocuments,
    GetCandidateProfileForRecruiterQuery,
    GetCandidateProfileForRecruiterQueryVariables,
} from '__generated__/graphql'
import type { ApolloQueryRefetchFunction } from 'shared/types/graphql'

interface Props {
    loading?: boolean
    documents: CompanyDocuments[]
    refetch: ApolloQueryRefetchFunction<
        GetCandidateProfileForRecruiterQuery,
        GetCandidateProfileForRecruiterQueryVariables
    >
    baseStyle: React.CSSProperties
    onClose?: () => void
}

const DocumentUpload: React.FC<Props> = ({
    documents,
    refetch,
    baseStyle,
    onClose,
}) => {
    const toast = useToast()
    const isMobile = useIsMobile()
    const [open, setOpen] = useState<boolean>(false)
    const [documentId, setDocumentId] = useState<string>('')
    const [message, setMessage] = useState<string>('')
    const [uploadLoading, setUploadLoading] = useState<boolean>(false)
    const [title, setTitle] = useState<string>('')
    const [action, setAction] = useState<string>('')
    const [deleteMutation] = useMutation(DeleteCompanyDocument)
    const { companyId } = useParams()
    const [download] = useLazyQuery(DownloadCompanyDocument, {
        fetchPolicy: 'network-only',
    })

    const uploadDocument = useCallback(
        async (file: File) => {
            setUploadLoading(true)
            const formData = new FormData()
            formData.append('companyId', companyId?.toString() ?? '')
            formData.append('document', file)
            try {
                const response = await fetch(
                    `${config.api.uri}/companyDocumentUpload`,
                    {
                        method: 'POST',
                        body: formData,
                        headers: {
                            contentType: 'multipart/form-data',
                            Authorization:
                                'Bearer ' + localStorage.getItem('token'),
                            boundary: '----WebKitFormBoundary7MA4YWxkTrZu0gW',
                        },
                    }
                )
                const data = await response.json()
                if (response.ok) {
                    console.log('Document uploaded successfully: ', data)
                    refetch()
                    toast({
                        title: 'Document Uploaded',
                        message: `${file.name} has been uploaded`,
                        severity: 'success',
                    })
                } else {
                    console.error('Failed to upload document: ', data)
                    toast({
                        title: 'Something went wrong',
                        message: 'Please try again',
                    })
                }
            } catch (error) {
                console.error('Failed to upload document', error)
                toast({
                    title: 'Something went wrong',
                    message: 'Please try again',
                })
            }
            setUploadLoading(false)
        },
        [companyId, refetch, toast]
    )

    const onDrop = useCallback(
        (acceptedFiles) => {
            acceptedFiles.forEach((file: File) => {
                uploadDocument(file)
            })
        },
        [uploadDocument]
    )

    const accept = {
        'application/msword': ['.doc'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            ['.docx'],
        'application/pdf': ['.pdf'],
    }
    const { fileRejections, getRootProps, getInputProps } = useDropzone({
        accept,
        maxSize: 3000000,
        onDrop,
        multiple: false,
    })

    const fileRejectionItems = fileRejections.map(({ file, errors }) => {
        const FileRejectionMessage = ({
            code,
            message,
        }: {
            code: string
            message: string
        }) => {
            switch (code) {
                case ErrorCode.FileInvalidType:
                    const acceptedTypes = Object.values(accept)
                        .flat()
                        .join(', ')
                    return <>File must be type: {acceptedTypes}</>
                case ErrorCode.FileTooLarge:
                case ErrorCode.FileTooSmall:
                case ErrorCode.TooManyFiles:
                    return <>{message}</>
            }
        }

        return (
            <Stack sx={{}}>
                <Alert severity="error">
                    <AlertTitle>Error</AlertTitle>
                    <ul>
                        {errors.map((e) => (
                            <>
                                <li key={e.code}>
                                    {file.name} -{' '}
                                    <FileRejectionMessage
                                        code={e.code}
                                        message={e.message}
                                    />
                                </li>
                            </>
                        ))}
                    </ul>
                </Alert>
            </Stack>
        )
    })

    const formatDocumentKey = (docKey: string) => {
        if (docKey.includes('/')) {
            const splitDocKey = docKey.split('/')
            return splitDocKey[splitDocKey.length - 1]
        } else {
            return docKey
        }
    }

    const handleCloseModal = () => {
        setOpen(false)
        setDocumentId('')
        setAction('')
        setTitle('')
        setMessage('')
    }

    const handleDownloadClick = async (id: string) => {
        try {
            const { data } = await download({ variables: { documentId: id } })
            console.log(data?.downloadCompanyDocument?.url)
            if (data?.downloadCompanyDocument?.url) {
                const hiddenDownloadLink = document.createElement('a')
                hiddenDownloadLink.href = data.downloadCompanyDocument.url
                hiddenDownloadLink.target = '_blank'
                hiddenDownloadLink.click()
                hiddenDownloadLink.remove()
            } else {
                toast({
                    title: 'Something went wrong',
                    message: 'Please try again later.',
                })
            }
        } catch (error) {
            console.log(error)
            toast({
                title: 'Something went wrong',
                message: 'Please try again later.',
            })
        }
    }

    const handleDeleteIconClick = (id: string) => {
        setDocumentId(id)
        setOpen(true)
        setMessage('This file will be permanently removed.')
        setTitle('Are you sure?')
        setAction('delete')
    }

    const handleConfirmModal = async () => {
        try {
            if (action === 'delete') {
                await deleteMutation({ variables: { documentId } })
            }
            refetch()
            setOpen(false)
            setDocumentId('')
            setAction('')
            setTitle('')
            setMessage('')
        } catch (error) {
            console.log(error)
            toast({
                title: 'Something went wrong',
                message: 'Please try again later.',
            })
        }
    }
    return (
        <Box sx={{ borderRadius: 2 }}>
            <ConfirmDialog
                open={open}
                handleClose={handleCloseModal}
                handleConfirm={handleConfirmModal}
                message={message}
                title={title}
            />
            <Box
                sx={{
                    ...baseStyle,
                }}
            >
                <div
                    {...getRootProps()}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <input {...getInputProps()} />
                    <IconButton size="small">
                        <UploadFile color="primary" />
                    </IconButton>
                    <Typography sx={{ px: getPxAsRem(4) }}>
                        <Link underline="hover" color="primary">
                            Click to upload
                        </Link>
                        &nbsp; or drag and drop
                    </Typography>
                    <Typography>.doc, .docx, .pdf</Typography>
                </div>
            </Box>
            {fileRejectionItems.length > 0 && (
                <div style={{ marginTop: getPxAsRem(28) }}>
                    {fileRejectionItems}
                </div>
            )}
            {uploadLoading && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: getPxAsRem(64),
                    }}
                >
                    <CircularProgress />
                </Box>
            )}
            {documents && documents.length > 0 ? (
                <Grid
                    container
                    sx={{
                        marginTop: getPxAsRem(12),
                    }}
                >
                    {documents?.map((doc, index) => (
                        <Fragment key={`doc_${doc.id}`}>
                            <Grid
                                item
                                xs={12}
                                sx={{
                                    borderWidth: 1,
                                    borderRadius: 1,
                                    border: `1px solid ${theme.palette.grey[300]}`,
                                    p: 2,
                                    my: 1,
                                }}
                            >
                                <Stack
                                    direction={isMobile ? 'column' : 'row'}
                                    justifyContent={
                                        isMobile ? 'center' : 'space-between'
                                    }
                                    alignItems={
                                        isMobile ? 'flex-start' : 'center'
                                    }
                                    rowGap={1}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <UploadFile
                                            color="primary"
                                            fontSize="small"
                                        />
                                        <Box sx={{ py: getPxAsRem(8) }}>
                                            <Typography>
                                                {formatDocumentKey(
                                                    doc.documentKey
                                                )}{' '}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Stack
                                        direction={
                                            isMobile ? 'row-reverse' : 'row'
                                        }
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-around',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleDownloadClick(doc.id)
                                                }
                                            >
                                                <Download fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleDeleteIconClick(
                                                        doc.id
                                                    )
                                                }
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Stack>
                                </Stack>
                            </Grid>
                        </Fragment>
                    ))}
                </Grid>
            ) : (
                ''
            )}
        </Box>
    )
}

export default DocumentUpload
